import {
  ComponentType,
  LazyExoticComponent,
  Suspense,
  useEffect,
  useState
} from 'react';
import lazyDelayedImport from 'utils/lazyDelayedImport';
import { webviewRoutes } from './navigationRoutes';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import EmbeddedPageLayout from 'navigation/EmbeddedPageLayout';
import Loading from 'components/common/Loading';
import { randomLoadingMessage } from 'utils/routes.util';
import { getLocalStorage, removeLocalStorage } from 'utils/helpers/storage';
import { LOCAL_STORAGE } from 'constants/general';
import { WEBVIEW_ROUTES } from 'constants/routes';
import { capturePostHogEvent } from 'services/posthog.service';
import { POST_HOG_EVENT, WEBVIEW_EVENT } from 'constants/events';
import { registerGlobalAppMetadataHandlers } from 'bridge';
import { WebviewNavigateDetail } from 'interfaces/bridge.interface';

const EmbeddedRoutes = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [Component, setComponent] = useState<LazyExoticComponent<
    ComponentType<object>
  > | null>(null);

  useEffect(() => {
    registerGlobalAppMetadataHandlers();
  }, []);

  // Bridges window.fbNavigate (fired outside React by the native shell) into a
  // react-router navigation so the screen swaps client-side without a full reload.
  useEffect(() => {
    const handleNavigate = (event: Event) => {
      const { path } = (event as CustomEvent<WebviewNavigateDetail>).detail;
      navigate(path);
    };
    window.addEventListener(WEBVIEW_EVENT.NAVIGATE, handleNavigate);
    return () => {
      window.removeEventListener(WEBVIEW_EVENT.NAVIGATE, handleNavigate);
    };
  }, [navigate]);

  useEffect(() => {
    if (pathname === WEBVIEW_ROUTES.WEBVIEW_ROUTINE_SUGGESTION) {
      navigate(WEBVIEW_ROUTES.WEBVIEW_ROUTINE_SUGGESTION_REVAMP, {
        replace: true
      });
      return;
    }

    let isCancelled = false;

    const loadComponent = async () => {
      setComponent(null);

      try {
        if (pathname === WEBVIEW_ROUTES.WEBVIEW_MARKETPLACE) {
          if (!isCancelled) {
            setComponent(() =>
              lazyDelayedImport(() => import('pages/marketplace'))
            );
          }
          return;
        }

        const isStripeManageSubscriptionRedirect =
          getLocalStorage(
            LOCAL_STORAGE.REDIRECT_TO_MANAGE_SUBSCRIPTION_VIA_WEBVIEW
          ) === WEBVIEW_ROUTES.WEBVIEW_MANAGE_SUBSCRIPTION;

        if (isStripeManageSubscriptionRedirect) {
          capturePostHogEvent(
            POST_HOG_EVENT.REDIRECT_TO_MANAGE_SUBSCRIPTION_VIA_WEBVIEW
          );
          if (!isCancelled) {
            setComponent(() =>
              lazyDelayedImport(() => import('pages/subscription'))
            );
          }
          removeLocalStorage([
            LOCAL_STORAGE.REDIRECT_TO_MANAGE_SUBSCRIPTION_VIA_WEBVIEW
          ]);
          return;
        }

        const matchedRoute =
          webviewRoutes.find((route) =>
            route.matchPattern
              ? matchPath(route.path, pathname)
              : route.path === pathname
          ) || webviewRoutes.find((route) => route.path === '');

        if (!matchedRoute) {
          if (!isCancelled) {
            setComponent(() =>
              lazyDelayedImport(() => import('pages/other/NotFoundPage'))
            );
          }
          return;
        }

        const module = await matchedRoute.Component;
        if (isCancelled) {
          return;
        }

        const LazyComponent = lazyDelayedImport(() => Promise.resolve(module));
        setComponent(() => LazyComponent);
      } catch {
        if (!isCancelled) {
          setComponent(() =>
            lazyDelayedImport(() => import('pages/other/ErrorPage'))
          );
        }
      }
    };

    void loadComponent();

    return () => {
      isCancelled = true;
    };
  }, [pathname, navigate]);

  return (
    <EmbeddedPageLayout>
      <Suspense fallback={<Loading title={randomLoadingMessage()} />}>
        {Component ? <Component /> : <Loading title={randomLoadingMessage()} />}
      </Suspense>
    </EmbeddedPageLayout>
  );
};

export default EmbeddedRoutes;
